import { cn } from "@/lib/utils";

/**
 * Ni² system icon — the assistant identity across NeuroNeeti.
 * Rounded-square macOS-style icon with midnight-blue glass background,
 * thin border, warm-white "Ni" with a restrained orange superscript "²".
 *
 * Sizes: 20, 24, 28, 32, 40, 56, 72 (px).
 */
export default function Ni2Icon({ size = 24, className, subtle = false, ...rest }) {
  const radius = Math.max(5, Math.round(size * 0.28));
  const glyph = Math.round(size * 0.46);
  const sup = Math.round(size * 0.28);
  const borderWidth = size >= 32 ? 1.25 : 1;

  return (
    <span
      className={cn(
        "relative inline-flex shrink-0 select-none items-center justify-center overflow-hidden",
        className
      )}
      style={{
        width: size,
        height: size,
        borderRadius: radius,
        background:
          "linear-gradient(160deg, #1e2c4c 0%, #101b34 55%, #0a1226 100%)",
        border: `${borderWidth}px solid rgba(190, 210, 245, ${subtle ? 0.12 : 0.18})`,
        boxShadow: subtle
          ? "inset 0 1px 0 rgba(255,255,255,0.09), inset 0 -1px 0 rgba(0,0,0,0.35)"
          : "inset 0 1px 0 rgba(255,255,255,0.14), inset 0 -1px 0 rgba(0,0,0,0.45), 0 1px 2px rgba(0,0,0,0.25)",
      }}
      aria-label="Ni²"
      {...rest}
    >
      {/* Soft inner blue highlight */}
      <span
        aria-hidden="true"
        style={{
          position: "absolute",
          inset: 0,
          borderRadius: radius,
          background:
            "radial-gradient(ellipse 80% 60% at 50% -15%, rgba(140, 180, 250, 0.24), transparent 65%)",
          pointerEvents: "none",
        }}
      />
      {/* Glyph */}
      <span
        style={{
          position: "relative",
          fontFamily:
            '-apple-system, BlinkMacSystemFont, "SF Pro Display", "Inter Tight", sans-serif',
          fontWeight: 600,
          fontSize: glyph,
          lineHeight: 1,
          letterSpacing: "-0.03em",
          color: "rgba(255, 250, 244, 0.98)",
          textShadow: "0 1px 0 rgba(0,0,0,0.35)",
          display: "inline-flex",
          alignItems: "baseline",
        }}
      >
        Ni
        <sup
          style={{
            fontSize: sup,
            lineHeight: 1,
            marginLeft: 0.5,
            transform: `translateY(-${Math.round(glyph * 0.28)}px)`,
            color: "hsl(var(--nn-amber))",
            fontWeight: 700,
            letterSpacing: 0,
          }}
        >
          2
        </sup>
      </span>
    </span>
  );
}
